import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import NavBar from '../NavBar';
import Svg from '../Svg';
import SupportButton from '../SupportButton/SupportButton';
import NavigationButton from '../NavigationButton/NavigationButton';
import Icon from '../Icon';
import { PatientInfoVisibility } from '../../types/PatientInfoVisibility';
import SupportDialog from '../SupportDialog/SupportDialog';

interface HeaderProps {
  children?: React.ReactNode;
  menuOptions?: { title: string; icon?: string; onClick: () => void }[];
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: any;
  showPatientInfo?: PatientInfoVisibility;
  servicesManager?: any;
  Secondary?: React.ReactNode;
  appConfig?: any;
  browserUrl?: string;
  viewer3dUrl?: string;
}

const Header: React.FC<HeaderProps> = ({
  children,
  menuOptions,
  isReturnEnabled,
  onClickReturnButton,
  isSticky,
  WhiteLabeling,
  showPatientInfo = PatientInfoVisibility.VISIBLE_COLLAPSED,
  servicesManager,
  Secondary,
  appConfig,
  browserUrl,
  viewer3dUrl,
  ...props
}) => {
  const { t } = useTranslation('Header');

  const [isSupportDialogOpen, setIsSupportDialogOpen] = useState(false);

  const handleSupportSubmit = (data: any) => {
    console.log('Send Message API:', data);
  };

  const { uiNotificationService } = servicesManager.services;

  const finalBrowserUrl = browserUrl || appConfig?.browserUrl || '';
  const finalViewer3dUrl = viewer3dUrl || appConfig?.viewer3dUrl || '';

  return (
    <NavBar
      isSticky={isSticky}
      {...props}
      className="bg-[#d9d9d9]"
    >
      <div className="relative flex h-[70px] items-center justify-between px-4">
        {/* Left */}
        <div className="flex-shrink-1 flex min-w-0 items-center">
          <div
            data-cy="return-to-work-list"
            className="flex min-w-0 cursor-pointer items-center space-x-2"
          >
            <div className="flex flex-shrink-0 items-center">
              {WhiteLabeling?.createLogoComponentFn?.(React, props) || (
                <Svg
                  name="logo-vidaa-black"
                  className="h-7 w-auto" // Slightly reduced from h-8 to fit better
                />
              )}
            </div>

            {/* CHANGES:
          - Changed items-baseline to items-center to balance height alignment
          - Added leading-tight and text-[10px] for tighter space consumption
          - Added a max-width and hidden-rules to safeguard against central toolbar collisions
        */}
            <div className="truncate-container flex max-w-[150px] flex-col justify-center leading-tight md:max-w-[250px] xl:max-w-none">
              <span className="text-[10px] font-bold tracking-tight text-gray-800">
                RESEARCH USE ONLY.
              </span>
              <span className="hidden truncate text-[9px] text-gray-800 sm:inline xl:whitespace-normal">
                Not CE-certified. Not for clinical/diagnostic use.{' '}
                <strong>v{process.env.VERSION}</strong>
              </span>
            </div>

            {Secondary && (
              <div className="ml-3 hidden flex-shrink-0 border-l border-gray-400 pl-3 md:block">
                {Secondary}
              </div>
            )}
          </div>
        </div>

        {/* Center */}
        <div className="pointer-events-auto absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center space-x-2">
          {children}
        </div>

        {/* Right */}
        <div className="flex flex-shrink-0 items-center space-x-1 sm:space-x-2">
          <NavigationButton
            label="Browser"
            to={finalBrowserUrl}
            icon={<Icon name="tool-home" />}
            redirectType="browser"
          />

          <NavigationButton
            label="3D Viewer"
            to={finalViewer3dUrl}
            icon={<Icon name="tool-3d-viewer" />}
            redirectType="viewer"
          />

          <SupportButton onClick={() => setIsSupportDialogOpen(true)} />
        </div>
      </div>

      <SupportDialog
        isOpen={isSupportDialogOpen}
        onClose={() => setIsSupportDialogOpen(false)}
        onSubmit={handleSupportSubmit}
        uiNotificationService={uiNotificationService}
      />
    </NavBar>
  );
};

Header.propTypes = {
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      icon: PropTypes.string,
      onClick: PropTypes.func.isRequired,
    })
  ),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  isReturnEnabled: PropTypes.bool,
  isSticky: PropTypes.bool,
  onClickReturnButton: PropTypes.func,
  WhiteLabeling: PropTypes.object,
  showPatientInfo: PropTypes.string,
  servicesManager: PropTypes.object,
  browserUrl: PropTypes.string,
  viewer3dUrl: PropTypes.string,
};

Header.defaultProps = {
  isReturnEnabled: true,
  isSticky: false,
};

export default Header;
